import type { Component } from "solid-js";

interface Props {
  kind: "privacy" | "terms";
}

const LegalPage: Component<Props> = (props) => (
  <main class="legal-page">
    <article>
      <a href="/" class="legal-brand">
        Metripact meet
      </a>
      {props.kind === "privacy" ? (
        <>
          <h1>Privacy Notice</h1>
          <p>Last updated: August 3, 2026</p>
          <h2>About this notice</h2>
          <p>
            Metripact operates Metripact meet, an online scheduling application.
            This notice explains how Metripact meet accesses, uses, stores, protects,
            and shares personal information, including data received from Google
            APIs.
          </p>
          <h2>Information we process</h2>
          <p>
            We process host account details, availability settings, calendar
            connection credentials, booking details, participant email addresses,
            and information participants enter on a booking form. Technical
            information needed to authenticate requests, operate the service, and
            investigate errors or abuse may also be processed.
          </p>
          <h2>Why we process it</h2>
          <p>
            We use this information to display availability, create and manage
            calendar events and Google Meet links, send transactional messages,
            prevent double-booking, and secure the service.
          </p>
          <h2>Optional analytics and advertising measurement</h2>
          <p>
            If a visitor has granted optional analytics or advertising measurement
            consent on Metripact’s website, a first-party consent cookie allows this
            scheduling page to apply the same choice. After a new demo booking is
            confirmed, Metripact may send Google Analytics and Google Ads the random
            booking identifier and standard website measurement data so we can measure
            completed bookings. We do not send the participant’s name, email address,
            guests, or booking-form note in these events, and advertising
            personalization remains disabled. If optional consent was not granted,
            the Google measurement tag is not loaded.
          </p>
          <h2>Google user data we access, use, and store</h2>
          <p>
            With a host’s permission, Metripact meet accesses the following Google
            user data only to provide the sign-in and scheduling features requested
            by that host:
          </p>
          <ul>
            <li>
              <strong>Google account information:</strong> the account identifier,
              verified email address, display name, and profile picture are used and
              stored to authenticate the host and maintain their Metripact meet
              account.
            </li>
            <li>
              <strong>Primary calendar metadata:</strong> the calendar identifier,
              calendar email address, and timezone are used and stored to connect the
              correct calendar and present scheduling times accurately.
            </li>
            <li>
              <strong>Free/busy periods:</strong> start and end times for busy periods
              are retrieved when availability is calculated and are processed
              transiently to prevent double-booking. They are not stored. Metripact
              meet does not retrieve calendar event titles or descriptions when
              checking availability.
            </li>
            <li>
              <strong>Calendar events and Google Meet:</strong> Metripact meet sends
              meeting titles, descriptions, times, and participant addresses to
              Google to create, update, or delete events and generate Google Meet
              links. It stores the resulting event identifiers and Calendar and Meet
              links with the booking. It reads an event it created only when needed
              to obtain the resulting conference link.
            </li>
            <li>
              <strong>Authorization credentials:</strong> the Google refresh token
              is stored in encrypted form so the host’s calendar can remain
              connected. Short-lived access tokens are used only while making
              requested Google API calls and are not persistently stored.
            </li>
          </ul>
          <p>
            Google user data is used only to provide or improve these visible
            scheduling features and to keep them secure. It is not used for
            advertising, profiling, credit or lending decisions, or training
            general-purpose or personalized AI or machine-learning models.
            Metripact meet’s use and transfer of information received from Google
            APIs complies with the{" "}
            <a href="https://developers.google.com/terms/api-services-user-data-policy">
              Google API Services User Data Policy
            </a>
            , including its Limited Use requirements.
          </p>
          <h2>How and with whom we share Google user data</h2>
          <p>
            Metripact meet does not sell Google user data. We share, transfer, or
            disclose Google user data only to the following recipients and only for
            the purposes described:
          </p>
          <ul>
            <li>
              <strong>Google LLC:</strong> Metripact meet exchanges authorization
              credentials and calendar requests with Google APIs and sends the
              meeting and participant information described above to provide Google
              Calendar and Google Meet functionality.
            </li>
            <li>
              <strong>Meeting participants:</strong> the host, invitee, and guests
              receive the host’s identity, meeting details, calendar invitations,
              and Google Meet link as necessary to participate in the meeting the
              host or invitee requested.
            </li>
            <li>
              <strong>Brevo:</strong> our transactional email provider receives the
              recipient name and email address and the relevant booking and Google
              Meet details solely to deliver confirmations, reminders,
              rescheduling notices, and cancellations.
            </li>
            <li>
              <strong>Cloudflare:</strong> our network, security, and content
              delivery provider processes web traffic between a user and Metripact
              meet to deliver and protect the service.
            </li>
            <li>
              <strong>Hosting and database infrastructure providers:</strong> they
              process stored application data on Metripact’s behalf solely to host,
              operate, and secure Metripact meet. They may not use Google user data
              for their own purposes.
            </li>
          </ul>
          <p>
            Authorized Metripact personnel may access account or booking information
            only when needed to provide support, investigate security or abuse, or
            comply with applicable law. We may disclose information to a competent
            authority when legally required. We do not otherwise transfer or
            disclose Google user data to advertising platforms, data brokers,
            information resellers, or other third parties.
          </p>
          <h2>How we protect sensitive data</h2>
          <p>
            We use administrative, technical, and organizational safeguards designed
            to protect Google user data and other sensitive information against
            unauthorized access, use, alteration, loss, or disclosure. These
            safeguards include:
          </p>
          <ul>
            <li>
              HTTPS/TLS encryption for data sent between users, Metripact meet,
              Google, Cloudflare, and Brevo.
            </li>
            <li>
              AES-256-GCM authenticated encryption for stored Google refresh tokens
              and booking-management credentials, using a unique random nonce for
              each encrypted value.
            </li>
            <li>
              One-way hashing of server-side session credentials and Secure,
              HttpOnly, SameSite cookies in production.
            </li>
            <li>
              PKCE, one-time state values, limited OAuth state lifetimes, and
              short-lived access tokens for the Google authorization flow.
            </li>
            <li>
              Restricted administrative and database access, segregated application
              credentials, and least-privilege production workloads.
            </li>
            <li>
              Data minimization, including transient processing rather than storage
              of Google Calendar free/busy results.
            </li>
          </ul>
          <h2>Retention and rights</h2>
          <p>
            We retain account information, encrypted Google connection credentials,
            primary calendar metadata, event identifiers, and booking records only
            for as long as needed to operate the scheduling service, maintain
            security and audit records, meet legal obligations, or resolve disputes.
            Free/busy results and short-lived Google access tokens are not
            persistently stored. When information is no longer required, we delete
            or de-identify it.
          </p>
          <p>
            A host may revoke Metripact meet’s access at any time from their Google
            Account permissions. Hosts and participants may request access,
            correction, or deletion of their personal information by contacting us.
            Revoking Google access prevents future Google API access but does not by
            itself delete existing Metripact meet account or booking records; a
            deletion request can be made separately.
          </p>
          <h2>Contact</h2>
          <p>
            Privacy questions and requests can be sent to{" "}
            <a href="mailto:meetings@metripact.com">meetings@metripact.com</a>.
          </p>
        </>
      ) : (
        <>
          <h1>Participant Terms</h1>
          <p>Last updated: July 30, 2026</p>
          <h2>Scheduling</h2>
          <p>
            By scheduling, you confirm that the participant information you provide
            is accurate and that you are authorized to invite any additional guests.
          </p>
          <h2>Calendar invitations</h2>
          <p>
            The service creates a calendar event and conferencing link on behalf of
            the host. Invitation, update, reminder, and cancellation messages may be
            sent to all supplied participant addresses.
          </p>
          <h2>Cancellations and changes</h2>
          <p>
            A secure management link allows the holder to cancel or reschedule the
            meeting. Keep that link private.
          </p>
          <h2>Acceptable use</h2>
          <p>
            Do not use scheduling pages for abuse, unsolicited bulk invitations,
            impersonation, or unlawful activity.
          </p>
          <h2>Availability</h2>
          <p>
            The service is provided on an as-available basis. A booking is confirmed
            only after the confirmation screen is shown.
          </p>
        </>
      )}
    </article>
  </main>
);

export default LegalPage;
